import React, { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import PublicNavbar from "../components/PublicNavbar";
import Footer from "../components/Footer";
import { openInteractiveDemo } from "../components/demo/DemoLauncher";
import { HELP_ARTICLES, HELP_FAQ, ARTICLE_CATEGORIES } from "../data/helpContent";
import {
  Search, BookOpen, ArrowRight, ChevronDown, Clock, LifeBuoy, Mail, Play, Map,
} from "lucide-react";

const CAT_LABEL = Object.fromEntries(ARTICLE_CATEGORIES.map((c) => [c.key, c.label]));
const slugifyCat = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-");

export default function HelpCenter() {
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState("all");

  useEffect(() => {
    document.title = "Centre d'assistance | Junago";
  }, []);

  const q = query.trim().toLowerCase();

  const filteredArticles = useMemo(() => {
    return HELP_ARTICLES.filter((a) => {
      if (activeCat !== "all" && a.category !== activeCat) return false;
      if (!q) return true;
      return (a.title + " " + a.excerpt).toLowerCase().includes(q);
    });
  }, [q, activeCat]);

  const filteredFaq = useMemo(() => {
    if (!q) return HELP_FAQ;
    return HELP_FAQ.map((cat) => ({
      ...cat,
      items: cat.items.filter((it) => (it.q + " " + it.a).toLowerCase().includes(q)),
    })).filter((cat) => cat.items.length > 0);
  }, [q]);

  return (
    <div className="min-h-screen bg-white">
      <PublicNavbar />

      {/* ─── Hero + Search ─── */}
      <section className="relative bg-slate-950 text-white overflow-hidden" data-testid="help-hero">
        <div aria-hidden className="absolute -top-32 left-1/3 h-96 w-96 rounded-full bg-violet-600 opacity-25 blur-3xl" />
        <div aria-hidden className="absolute -bottom-40 -right-24 h-80 w-80 rounded-full bg-fuchsia-600 opacity-15 blur-3xl" />
        <div className="relative max-w-4xl mx-auto px-6 pt-16 md:pt-24 pb-16 md:pb-20 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-500/20 border border-violet-500/30 text-violet-200 text-[11px] font-bold tracking-[0.15em] uppercase mb-6">
            <LifeBuoy className="h-3 w-3" /> Centre d'assistance
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05]" data-testid="help-title">
            Comment pouvons-nous
            <br />
            <span className="text-violet-400">vous aider&nbsp;?</span>
          </h1>
          <p className="mt-4 text-base md:text-lg text-slate-400 max-w-xl mx-auto leading-relaxed">
            24 guides pratiques et plus de 30 réponses organisées par thème : démarrage, site de réservation, paiements, facturation, équipe, IA…
          </p>
          <div className="mt-8 max-w-xl mx-auto relative">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher un article ou une question…"
              className="w-full h-14 pl-13 pr-5 rounded-2xl bg-white/10 border border-white/15 text-white placeholder:text-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:bg-white/15 transition-all"
              style={{ paddingLeft: "3.25rem" }}
              data-testid="help-search-input"
            />
          </div>
        </div>
      </section>

      {/* ─── Bannière Roadmap ─── */}
      <section className="max-w-6xl mx-auto px-6 pt-10" data-testid="help-roadmap-banner">
        <Link
          to="/assistance/roadmap"
          className="group relative flex flex-col sm:flex-row sm:items-center gap-4 rounded-3xl bg-[#0B0B16] border border-white/10 p-6 sm:p-7 overflow-hidden hover:border-violet-500/40 transition-colors"
          data-testid="help-roadmap-link"
        >
          <div aria-hidden className="absolute -top-16 right-1/4 h-40 w-40 rounded-full bg-violet-600/30 blur-3xl" />
          <span className="relative h-11 w-11 rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center shrink-0">
            <Map className="h-5 w-5 text-white" />
          </span>
          <div className="relative flex-1">
            <div className="font-heading text-base font-semibold text-white">Roadmap produit — où va Junago</div>
            <p className="mt-1 text-sm text-slate-400 leading-relaxed">
              Nom de domaine personnalisé, place de marché, commandes vocales… et en 2028, l&apos;agent IA autonome qui agira à votre place. Découvrez la chronologie complète.
            </p>
          </div>
          <span className="relative inline-flex items-center gap-1.5 text-sm font-semibold text-violet-300 group-hover:text-white transition-colors shrink-0">
            Voir la roadmap <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
          </span>
        </Link>
      </section>

      {/* ─── Articles / Guides ─── */}
      <section className="max-w-6xl mx-auto px-6 py-16 md:py-24" data-testid="help-articles-section">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-violet-600 mb-2">Guides & fonctionnalités</div>
            <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight text-slate-900">
              Explorez la plateforme
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            <CatChip active={activeCat === "all"} onClick={() => setActiveCat("all")} testid="help-cat-all">
              Tous
            </CatChip>
            {ARTICLE_CATEGORIES.map((c) => (
              <CatChip key={c.key} active={activeCat === c.key} onClick={() => setActiveCat(c.key)} testid={`help-cat-${c.key}`}>
                {c.label}
              </CatChip>
            ))}
          </div>
        </div>

        {filteredArticles.length === 0 ? (
          <div className="text-center py-16 text-slate-500 text-sm" data-testid="help-no-articles">
            Aucun article ne correspond à votre recherche.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredArticles.map((a) => (
              <Link
                key={a.slug}
                to={`/assistance/article/${a.slug}`}
                className="group rounded-2xl border border-slate-200 bg-white p-7 hover:border-violet-300 hover:shadow-[0_12px_32px_-12px_rgba(124,58,237,0.25)] transition-all duration-200 flex flex-col"
                data-testid={`help-article-card-${a.slug}`}
              >
                <div className="flex items-center justify-between mb-5">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-violet-50 text-violet-700 text-[11px] font-semibold">
                    <BookOpen className="h-3 w-3" /> {CAT_LABEL[a.category]}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[11px] text-slate-400">
                    <Clock className="h-3 w-3" /> {a.readTime}
                  </span>
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-900 tracking-tight leading-snug group-hover:text-violet-700 transition-colors">
                  {a.title}
                </h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">{a.excerpt}</p>
                <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-violet-600">
                  Lire l'article
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* ─── FAQ générale ─── */}
      <section id="faq" className="scroll-mt-20 bg-slate-50 border-y border-slate-200" data-testid="help-faq-section">
        <div className="max-w-3xl mx-auto px-6 py-16 md:py-24">
          <div className="text-center mb-12">
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-violet-600 mb-2">FAQ générale</div>
            <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight text-slate-900">
              Questions fréquentes
            </h2>
          </div>
          {filteredFaq.length === 0 ? (
            <div className="text-center py-10 text-slate-500 text-sm" data-testid="help-no-faq">
              Aucune question ne correspond à votre recherche.
            </div>
          ) : (
            <>
              <div className="flex flex-wrap justify-center gap-2 mb-10" data-testid="faq-theme-nav">
                {filteredFaq.map((cat) => (
                  <a
                    key={cat.category}
                    href={`#faq-${slugifyCat(cat.category)}`}
                    className="px-3.5 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-semibold text-slate-600 hover:border-violet-300 hover:text-violet-700 transition-colors"
                    data-testid={`faq-theme-chip-${slugifyCat(cat.category)}`}
                  >
                    {cat.category}
                  </a>
                ))}
              </div>
              <div className="space-y-10">
                {filteredFaq.map((cat) => (
                  <div key={cat.category} id={`faq-${slugifyCat(cat.category)}`} className="scroll-mt-24" data-testid={`faq-category-${cat.category}`}>
                    <h3 className="font-heading text-lg font-semibold text-slate-900 mb-3">{cat.category}</h3>
                    <div className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100 overflow-hidden">
                      {cat.items.map((item) => (
                        <FaqRow key={item.q} item={item} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* ─── Contact CTA ─── */}
      <section className="max-w-5xl mx-auto px-6 py-16 md:py-24" data-testid="help-contact-section">
        <div className="relative overflow-hidden rounded-3xl bg-slate-950 text-white px-8 sm:px-14 py-14">
          <div aria-hidden className="absolute -right-20 -bottom-32 h-96 w-96 rounded-full bg-violet-600 opacity-25 blur-3xl" />
          <div className="relative grid md:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight leading-tight">
                Vous n'avez pas trouvé votre réponse&nbsp;?
              </h2>
              <p className="mt-3 text-slate-400 leading-relaxed">
                Notre équipe vous répond en français, du lundi au vendredi. Vous pouvez aussi explorer la démo interactive pour découvrir la plateforme.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <a
                href="mailto:support@junago.fr"
                className="btn-jn-light inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold"
                data-testid="help-contact-email"
              >
                <Mail className="h-4 w-4" /> Contacter le support
              </a>
              <button type="button" onClick={openInteractiveDemo} className="btn-jn-play-wrap" data-testid="help-contact-demo">
                <span className="btn-jn-play btn-jn-play--light"><Play className="h-4 w-4 fill-current" /></span>
                <span className="btn-jn-play-label btn-jn-play-label--light">Voir la démo interactive</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function CatChip({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2 rounded-full text-xs font-semibold transition-all ${
        active
          ? "bg-slate-900 text-white shadow-sm"
          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
      }`}
    >
      {children}
    </button>
  );
}

export function FaqRow({ item, dark = false }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center justify-between gap-4 px-6 py-5 text-left transition-colors ${
          dark ? "hover:bg-white/5" : "hover:bg-slate-50"
        }`}
        data-testid={`faq-question-${item.q.slice(0, 30)}`}
        aria-expanded={open}
      >
        <span className={`text-sm font-semibold ${dark ? "text-white" : "text-slate-900"}`}>{item.q}</span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 transition-transform duration-200 ${
            dark ? "text-slate-400" : "text-slate-400"
          } ${open ? "rotate-180" : ""}`}
        />
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-200 ease-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
      >
        <div className="overflow-hidden">
          <p className={`px-6 pb-5 text-sm leading-relaxed ${dark ? "text-slate-400" : "text-slate-600"}`}>
            {item.a}
          </p>
        </div>
      </div>
    </div>
  );
}
